import * as React from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { Box } from '@mui/system';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import type Organisation from 'benchmarking-domain/organisation';
import { DataGridArrayColumnSortComparator, DataGridInMemory } from 'components/core/shared/data-grid-in-memory';
import { nameOf } from 'helpers/nameof';
import { useNavigate } from 'react-router-dom';

import { paths } from '../../../paths';
import { OrganisationArchiveButton } from './organisation-archive-confirmation';

const OrganisationArchiveButtonMemo = React.memo(OrganisationArchiveButton);

interface OrganisationsTableProps {
  rows?: Organisation[];
}

export function OrganisationsTable({ rows = [] }: OrganisationsTableProps): React.JSX.Element {
  const navigate = useNavigate();

  const columns: GridColDef[] = [
    {
      field: nameOf<Organisation>('name'),
      headerName: 'Name',
      minWidth: 200,
    },
    {
      field: nameOf<Organisation>('managers'),
      headerName: 'Managers',
      flex: 1,
      minWidth: 200,
      sortComparator: DataGridArrayColumnSortComparator,
      renderCell: (params: GridRenderCellParams<Organisation, string[]>) => (
        <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 1, padding: 1 }}>
          {params.value?.map((manager) => <Chip key={manager} color="default" label={manager} size="small" />)}
        </Box>
      ),
    },
    {
      field: nameOf<Organisation>('domains'),
      headerName: 'Domains',
      flex: 1,
      minWidth: 200,
      sortComparator: DataGridArrayColumnSortComparator,
      renderCell: (params: GridRenderCellParams<Organisation, string[]>) => (
        <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 1, padding: 1 }}>
          {params.value?.map((domain) => <Chip key={domain} color="default" label={domain} size="small" />)}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 350,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<Organisation>) => {
        return (
          <Stack direction="row" spacing={1} sx={{ marginY: 1 }}>
            <Button
              variant="contained"
              onClick={() => {
                navigate(paths.app.admin.organisations.getDetailsUrl(params.row.id));
              }}
            >
              View&nbsp;organisation
            </Button>
            <OrganisationArchiveButtonMemo organisationId={params.row.id} />
          </Stack>
        );
      },
    },
  ];

  return <DataGridInMemory columns={columns} rows={rows} searchPlaceholder="Search organisations" />;
}
